/**
 * @fileoverview Schedule Optimization Tab
 * @description 5 fixed accordion slots for queuing optimization jobs.
 *              Each slot can be empty (upload), queued, in_progress, completed, or failed.
 *              Completed slots show full review UI with accept/reject + finalize/download.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Chip, Box, LinearProgress,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { regressionOptimizerApi } from '../services/regressionOptimizerApi';

// ── Helpers ──

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtElapsed(start, end) {
  if (!start) return '';
  const sec = Math.round(((end || Date.now() / 1000) - start));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

const STATUS_CONFIG = {
  available: { label: 'Available', color: '#94a3b8', bg: '#f1f5f9', border: '1px dashed #cbd5e1' },
  queued: { label: 'Queued', color: '#f59e0b', bg: '#fffbeb', border: '1px solid #fde68a' },
  in_progress: { label: 'In Progress', color: '#3b82f6', bg: '#eff6ff', border: '1px solid #bfdbfe' },
  completed: { label: 'Completed', color: '#10b981', bg: '#ecfdf5', border: '1px solid #a7f3d0' },
  failed: { label: 'Failed', color: '#ef4444', bg: '#fef2f2', border: '1px solid #fecaca' },
};

const SLOT_LABELS = ['Suite 1', 'Suite 2', 'Suite 3', 'Suite 4', 'Suite 5'];

// ── Main Component ──

export const ScheduleOptimization = () => {
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const prevStatusRef = useRef({}); // { jobId: status }
  const [toasts, setToasts] = useState([]); // [{ id, message, type, visible }]

  const addToast = useCallback((message, type = 'info') => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, message, type, visible: true }]);
    setTimeout(() => setToasts(prev => prev.map(t => t.id === id ? { ...t, visible: false } : t)), 3500);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const TOAST_MESSAGES = {
    queued: { msg: 'has been queued for optimization', type: 'info' },
    in_progress: { msg: 'optimization started', type: 'info' },
    completed: { msg: 'optimization completed successfully', type: 'success' },
    failed: { msg: 'optimization failed', type: 'error' },
  };

  const fetchJobs = useCallback(async () => {
    try {
      const data = await regressionOptimizerApi.listScheduledJobs();
      const newJobs = data.jobs || [];

      // Detect status changes and fire toasts
      const prev = prevStatusRef.current;
      const newMap = {};
      newJobs.forEach((job, idx) => {
        newMap[job.id] = job.status;
        const oldStatus = prev[job.id];
        if (oldStatus && oldStatus !== job.status && TOAST_MESSAGES[job.status]) {
          const label = SLOT_LABELS[idx] || `Suite ${idx + 1}`;
          const t = TOAST_MESSAGES[job.status];
          addToast(`${label}: ${t.msg}`, t.type);
        }
      });
      prevStatusRef.current = newMap;

      setJobs(newJobs);
    } catch { /* silent retry */ }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => {
    fetchJobs();
    pollRef.current = setInterval(fetchJobs, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchJobs]);

  // Jobs come sorted by created_at ascending (position preserved)
  const slots = [];
  for (let i = 0; i < 5; i++) slots.push(jobs[i] || null);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--ro-text-primary, #1e293b)' }}>
          Schedule Optimization
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ro-text-muted, #64748b)' }}>
          Queue up to 5 test suites for background processing. Results will be ready when you return.
        </p>
      </div>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>Loading...</Box>
      ) : (
        slots.map((job, idx) => (
          <SlotAccordion
            key={job?.id || `empty-${idx}`}
            slotIndex={idx}
            job={job}
            expanded={expanded === idx}
            onChange={handleAccordionChange(idx)}
            onRefresh={fetchJobs}
          />
        ))
      )}

      {/* Toast notifications */}
      {toasts.map((t, i) => (
        <div key={t.id} className={`ro-toast ${t.type} ${t.visible ? 'visible' : ''}`}
          style={{ bottom: 32 + i * 52 }}>
          {t.message}
        </div>
      ))}
    </div>
  );
};

// ── Slot Accordion ──

function SlotAccordion({ slotIndex, job, expanded, onChange, onRefresh }) {
  const status = job?.status || 'available';
  const cfg = STATUS_CONFIG[status];

  // Always use Suite N as primary label; file/ADO info is subtitle
  const slotLabel = SLOT_LABELS[slotIndex];
  const sourceInfo = job?.display_name || job?.filename || '';

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        mb: 1.5, borderRadius: '12px !important',
        border: cfg.border,
        boxShadow: expanded ? '0 4px 20px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.04)',
        '&:before': { display: 'none' },
        overflow: 'hidden', transition: 'box-shadow 0.2s',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2, my: 1 } }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: status === 'available' ? '#f1f5f9' : `${cfg.color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: cfg.color, flexShrink: 0,
        }}>
          {slotIndex + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {slotLabel}
          </div>
          {job && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {sourceInfo && <span style={{ marginRight: 6 }}>{sourceInfo} &bull;</span>}
              {status === 'in_progress' ? `Processing for ${fmtElapsed(job.started_at, null)}...`
                : status === 'completed' ? `Completed ${fmtTime(job.completed_at)}`
                : status === 'failed' ? `Failed ${fmtTime(job.completed_at)}`
                : `Queued ${fmtTime(job.created_at)}`}
            </div>
          )}
        </div>
        <Chip label={cfg.label} size="small" sx={{
          fontWeight: 600, fontSize: 11, color: cfg.color, bgcolor: cfg.bg,
          border: `1px solid ${cfg.color}30`, height: 26,
        }} />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        {status === 'in_progress' && (
          <LinearProgress sx={{ mb: 2, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
        )}
        <SlotContent job={job} status={status} onRefresh={onRefresh} slotIndex={slotIndex} />
      </AccordionDetails>
    </Accordion>
  );
}

// ── Slot Content Dispatcher ──

function SlotContent({ job, status, onRefresh, slotIndex }) {
  if (status === 'available') return <EmptySlot onRefresh={onRefresh} />;
  if (status === 'queued') return <QueuedSlot job={job} onRefresh={onRefresh} />;
  if (status === 'in_progress') return <InProgressSlot job={job} />;
  if (status === 'completed') return <CompletedSlot job={job} onRefresh={onRefresh} slotIndex={slotIndex} />;
  if (status === 'failed') return <FailedSlot job={job} onRefresh={onRefresh} />;
  return null;
}

// ── Empty Slot ──

function EmptySlot({ onRefresh }) {
  const [uploadMode, setUploadMode] = useState('ado');
  const [adoUrl, setAdoUrl] = useState('');
  const [file, setFile] = useState(null);
  const [threshold, setThreshold] = useState(0.96);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Domain auto-detection & reference upload (mirrors Optimize Now)
  const [classifying, setClassifying] = useState(false);
  const [detectedDomain, setDetectedDomain] = useState(null); // { name, display_name }
  const [showRefUpload, setShowRefUpload] = useState(false);
  const [refFiles, setRefFiles] = useState([]);
  const [uploadingRef, setUploadingRef] = useState(false);
  const refInputRef = useRef(null);

  // Auto-classify when file is selected
  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setError('');
    setDetectedDomain(null);
    setShowRefUpload(false);
    setRefFiles([]);
    if (!selectedFile) return;

    setClassifying(true);
    try {
      const result = await regressionOptimizerApi.classify(selectedFile);
      const domainAgent = result.domain_agent;
      const domainDisplayName = result.domain_display_name;
      if (domainAgent) {
        setDetectedDomain({
          name: domainAgent,
          display_name: domainDisplayName || domainAgent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        });
        setShowRefUpload(true);
      }
    } catch {
      // Classification failed — non-blocking, proceed without domain
    } finally { setClassifying(false); }
  };

  const handleRefUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !detectedDomain) return;
    setUploadingRef(true);
    try {
      const names = [];
      for (const f of files) {
        await regressionOptimizerApi.uploadTraining(detectedDomain.name, f);
        names.push(f.name);
      }
      setRefFiles(prev => [...prev, ...names]);
    } catch (err) {
      setError(err.message || 'Reference upload failed');
    } finally { setUploadingRef(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const config = { threshold };
      if (detectedDomain) config.domainAgent = detectedDomain.name;
      if (uploadMode === 'ado') {
        if (!adoUrl.trim()) return;
        await regressionOptimizerApi.scheduleJobAdo(adoUrl.trim(), config);
      } else {
        if (!file) return;
        await regressionOptimizerApi.scheduleJobFile(file, config);
      }
      setAdoUrl(''); setFile(null); setRefFiles([]); setDetectedDomain(null); setShowRefUpload(false);
      onRefresh();
    } catch (err) {
      setError(err.message || 'Failed to submit job');
    } finally { setSubmitting(false); }
  };

  const canSubmit = uploadMode === 'ado' ? adoUrl.trim() : file;

  return (
    <div>
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
        {[{ key: 'ado', label: 'Azure DevOps' }, { key: 'file', label: 'Upload File' }].map(tab => (
          <button key={tab.key} onClick={() => setUploadMode(tab.key)} style={{
            flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: 'none', background: 'transparent',
            color: uploadMode === tab.key ? '#4f46e5' : '#64748b',
            borderBottom: uploadMode === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: '-2px', transition: 'all 0.2s',
          }}>{tab.label}</button>
        ))}
      </div>
      {uploadMode === 'ado' && (
        <input type="text" placeholder="https://dev.azure.com/org/project/_testPlans/define?planId=...&suiteId=..."
          value={adoUrl} onChange={e => { setAdoUrl(e.target.value); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
          disabled={submitting}
          style={{ width: '100%', padding: '10px 14px', fontSize: 13, borderRadius: 8, border: '1.5px solid #cbd5e1', outline: 'none', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }}
        />
      )}
      {uploadMode === 'file' && (
        <div>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); }} />
          {!file ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
              role="button"
              tabIndex={0}
              style={{ padding: '24px 16px', border: '2px dashed #cbd5e1', borderRadius: 10, textAlign: 'center', cursor: 'pointer', color: '#64748b', fontSize: 13 }}>
              <strong>Click to upload</strong> &bull; .xlsx / .xls
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{file.name}</span>
              {classifying && <span className="ro-btn-spinner" style={{ width: 14, height: 14, borderColor: 'rgba(79,70,229,0.3)', borderTopColor: '#4f46e5' }}></span>}
              <button onClick={() => { setFile(null); setDetectedDomain(null); setShowRefUpload(false); setRefFiles([]); }} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Remove</button>
            </div>
          )}
        </div>
      )}
      <div style={{ margin: '16px 0 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Similarity Threshold: {Math.round(threshold * 100)}%</label>
        <input type="range" min="0.80" max="1.00" step="0.01" value={threshold} onChange={e => setThreshold(parseFloat(e.target.value))} style={{ flex: 1, accentColor: '#4f46e5' }} />
      </div>

      {/* Domain detected — Reference Upload (auto-detected, mirrors Optimize Now) */}
      {showRefUpload && detectedDomain && (
        <div style={{ margin: '12px 0 16px', padding: '14px 16px', borderRadius: 10, border: '1px solid rgba(0,151,169,0.2)', background: 'linear-gradient(135deg, rgba(0,151,169,0.03), rgba(1,66,106,0.01))' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(135deg, #0097A9, #01426A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10H12V2z"/><path d="M12 12 12 2a10 10 0 0 1 10 10z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{detectedDomain.display_name}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Domain detected from your test suite</div>
            </div>
          </div>

          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
            For better optimization results, upload reference documents about this domain.
          </p>

          <input ref={refInputRef} id="ref-file-input" type="file" accept=".pdf,.docx,.doc,.txt,.md" multiple style={{ display: 'none' }}
            onChange={handleRefUpload} />

          {uploadingRef ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '2px dashed #94a3b8', background: '#f8fafc', color: '#64748b' }}>
              <span className="ro-btn-spinner" style={{ width: 14, height: 14, borderColor: 'rgba(0,151,169,0.3)', borderTopColor: '#0097A9' }}></span>
              Uploading...
            </div>
          ) : (
            <label htmlFor="ref-file-input" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 16px', fontSize: 13, fontWeight: 700, borderRadius: 8,
              border: '2px dashed #0097A9', background: 'rgba(0,151,169,0.04)', color: '#0097A9',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Upload Reference Files (PDF, DOCX, TXT)
            </label>
          )}

          {refFiles.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {refFiles.map((name, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 12, background: 'rgba(0,175,102,0.08)', color: '#059669', border: '1px solid rgba(0,175,102,0.2)' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                  {name}
                </span>
              ))}
            </div>
          )}

          <button onClick={() => setShowRefUpload(false)} style={{
            marginTop: 10, width: '100%', padding: '9px 16px', fontSize: 12, fontWeight: 600, borderRadius: 8,
            border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer',
          }}>
            {refFiles.length > 0 ? 'Done' : 'Skip — Continue Without Uploading'}
          </button>

          <p style={{ margin: '8px 0 0', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
            Files are stored and reused for all future optimizations of this domain.
          </p>
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{error}</div>}
      <button onClick={handleSubmit} disabled={!canSubmit || submitting} style={{
        width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
        cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
        background: canSubmit && !submitting ? 'linear-gradient(135deg, #4f46e5, #6366f1)' : '#e2e8f0',
        color: canSubmit && !submitting ? '#fff' : '#94a3b8', transition: 'all 0.2s',
        boxShadow: canSubmit && !submitting ? '0 2px 8px rgba(79,70,229,0.25)' : 'none',
      }}>{submitting ? 'Submitting...' : 'Add to Queue'}</button>
    </div>
  );
}

// ── Queued Slot ──

function QueuedSlot({ job, onRefresh }) {
  const [cancelling, setCancelling] = useState(false);
  const handleCancel = async () => {
    setCancelling(true);
    try { await regressionOptimizerApi.deleteScheduledJob(job.id); onRefresh(); }
    catch { setCancelling(false); }
  };
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>&#9203;</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Waiting in queue</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
        Submitted {fmtTime(job.created_at)} &bull; Threshold: {Math.round((job.config?.threshold || 0.96) * 100)}%
      </div>
      <button onClick={handleCancel} disabled={cancelling} style={{
        padding: '8px 24px', fontSize: 13, fontWeight: 600, borderRadius: 8,
        border: '1px solid #fecaca', background: '#fef2f2', color: '#ef4444', cursor: cancelling ? 'not-allowed' : 'pointer',
      }}>{cancelling ? 'Cancelling...' : 'Cancel Job'}</button>
    </div>
  );
}

// ── In Progress Slot ──

function InProgressSlot({ job }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div className="ro-btn-spinner" style={{ width: 32, height: 32, margin: '0 auto 12px', borderWidth: 3 }}></div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Optimization in progress</div>
      <div style={{ fontSize: 12, color: '#64748b' }}>
        Started {fmtTime(job.started_at)} &bull; Running for {fmtElapsed(job.started_at, null)}
      </div>
    </div>
  );
}

// ── Completed Slot (Full Review UI) ──

function CompletedSlot({ job, onRefresh, slotIndex }) {
  const [resultData, setResultData] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);
  const [resultError, setResultError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [clearing, setClearing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const [decisions, setDecisions] = useState({ removals: {}, recommendations: {} });
  const [rejectFeedback, setRejectFeedback] = useState({});
  const [feedbackConfirmed, setFeedbackConfirmed] = useState({});

  const loadResult = useCallback(async () => {
    if (resultData) return;
    setLoadingResult(true);
    setResultError('');
    try {
      const data = await regressionOptimizerApi.getScheduledJob(job.id);
      if (data.job?.result_data) {
        setResultData(data.job.result_data);
      } else {
        setResultError('Results not available. The session may have expired.');
      }
    } catch (err) {
      setResultError(err.message || 'Failed to load results');
    } finally { setLoadingResult(false); }
  }, [job.id, resultData]);

  useEffect(() => { loadResult(); }, [loadResult]);

  const handleClear = async () => {
    setClearing(true);
    try { await regressionOptimizerApi.deleteScheduledJob(job.id); onRefresh(); }
    catch { setClearing(false); }
  };

  const toggleRemoval = (tcId) => {
    setDecisions(prev => ({
      ...prev,
      removals: {
        ...prev.removals,
        [tcId]: prev.removals[tcId] === 'accepted' ? 'rejected' : 'accepted',
      },
    }));
  };

  const toggleRecommendation = (idx) => {
    setDecisions(prev => ({
      ...prev,
      recommendations: {
        ...prev.recommendations,
        [idx]: prev.recommendations[idx] === 'accepted' ? 'rejected' : 'accepted',
      },
    }));
  };

  const handleFinalize = async () => {
    if (!resultData?.session_id) return;
    setFinalizing(true);
    try {
      const removedItems = resultData.removed || [];
      const idColKey = resultData.id_column;
      const acceptedRemovals = [];
      const rejectedRemovals = [];
      removedItems.forEach(r => {
        const tcId = idColKey ? String(r[idColKey] || '') : '';
        if (decisions.removals[tcId] === 'rejected') rejectedRemovals.push(tcId);
        else acceptedRemovals.push(tcId);
      });
      const acceptedRecs = [];
      const rejectedRecs = [];
      (resultData.recommendations || []).forEach((_, i) => {
        if (decisions.recommendations[i] === 'rejected') rejectedRecs.push(i);
        else acceptedRecs.push(i);
      });

      // Aggregate reject feedback
      const feedbackText = Object.entries(rejectFeedback)
        .filter(([, v]) => v?.trim())
        .map(([k, v]) => `[${k}] ${v}`)
        .join('\n');

      const blob = await regressionOptimizerApi.finalize({
        sessionId: resultData.session_id,
        acceptedRemovals,
        rejectedRemovals,
        acceptedRecommendations: acceptedRecs,
        rejectedRecommendations: rejectedRecs,
        outputFormat: 'bdd',
        userFeedbackText: feedbackText,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Optimized_${SLOT_LABELS[slotIndex] || 'TestSuite'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Download failed');
    } finally { setFinalizing(false); }
  };

  if (loadingResult) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748b' }}>
        <div className="ro-btn-spinner" style={{ width: 20, height: 20, margin: '0 auto 8px' }}></div>
        Loading results...
      </div>
    );
  }
  if (resultError) {
    return (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{resultError}</div>
        <button onClick={handleClear} style={{ fontSize: 12, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 16px', cursor: 'pointer' }}>Clear Slot</button>
      </div>
    );
  }
  if (!resultData) return null;

  const summary = resultData.summary;
  const removed = resultData.removed || [];
  const recommendations = resultData.recommendations || [];
  const idCol = resultData.id_column;
  const tokenUsage = resultData.token_usage || {};

  // Tally current decisions
  const removalCounts = { accepted: 0, rejected: 0 };
  removed.forEach(r => {
    const tcId = idCol ? String(r[idCol] || '') : '';
    const d = decisions.removals[tcId] || 'accepted';
    removalCounts[d]++;
  });
  const recCounts = { accepted: 0, rejected: 0 };
  recommendations.forEach((_, i) => {
    const d = decisions.recommendations[i] || 'accepted';
    recCounts[d]++;
  });

  // Cost savings computation
  const computeCostSavings = () => {
    const apiSignals = /\b(api|endpoint|status.?code|[1-5]\d{2}\s|200\b|201\b|204\b|400\b|401\b|403\b|404\b|500\b|502\b|rest\b|graphql|payload|json|xml|header|bearer|token|curl|postman|swagger)\b/gi;
    const uiSignals = /\b(page|screen|button|click|navigate|display|field|dropdown|modal|popup|checkbox|tab|menu|form|dialog|tooltip|hover|scroll|drag|select|radio|toggle|input|textarea|label|link|icon|table|grid|panel|sidebar|header|footer|banner|card|login\s*page|dashboard|verify.*visible|verify.*displayed|should.*see|ui|browser|dom|element)\b/gi;
    const classify = (text) => {
      if (!text) return 'ui';
      const a = (text.match(apiSignals) || []).length;
      const u = (text.match(uiSignals) || []).length;
      return a > u ? 'api' : 'ui';
    };
    let apiTcs = 0, uiTcs = 0;
    removed.forEach(r => {
      const tcId = idCol ? String(r[idCol] || '') : '';
      if ((decisions.removals[tcId] || 'accepted') === 'accepted') {
        const allText = Object.values(r).filter(v => typeof v === 'string').join(' ');
        if (classify(allText) === 'api') apiTcs++; else uiTcs++;
      }
    });
    recommendations.forEach((rec, i) => {
      if ((decisions.recommendations[i] || 'accepted') === 'accepted') {
        const reduced = Math.max(0, (rec.source_tc_ids || []).length - 1);
        const allText = [rec.reason, ...(rec.merged_steps || [])].filter(Boolean).join(' ');
        if (classify(allText) === 'api') apiTcs += reduced; else uiTcs += reduced;
      }
    });
    const hrs = (apiTcs / 15) * 8 + (uiTcs / 10) * 8;
    return { totalHoursSaved: hrs, dollarSavings: hrs * 23, apiTcs, uiTcs };
  };
  const savings = computeCostSavings();

  const totalSteps = summary?.original_step_count || 1;
  const originalCount = summary?.original_count || 1;
  const stepsRemovedPct = (() => {
    let steps = 0;
    recommendations.forEach((rec, i) => {
      if ((decisions.recommendations[i] || 'accepted') === 'accepted') {
        const srcCount = (rec.source_tc_ids || []).length;
        steps += Math.max(0, srcCount - 1) * Math.ceil(totalSteps / originalCount);
      }
    });
    steps += removalCounts.accepted * Math.ceil(totalSteps / originalCount);
    return totalSteps > 0 ? Math.round((steps / totalSteps) * 100) : 0;
  })();
  const totalSuggestions = removed.length + recommendations.length;
  const acceptanceRate = totalSuggestions > 0 ? Math.round(((recCounts.accepted + removalCounts.accepted) / totalSuggestions) * 100) : 0;

  return (
    <div>
      {/* ── Row 1: Input Analysis (static) ── */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#1e293b' }}>Input Analysis</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
          {[
            { label: 'Test Cases', value: summary?.original_count || 0, color: '#6366f1' },
            { label: 'Total Steps', value: summary?.original_step_count || 0, color: '#10b981' },
            { label: 'Total Suggestions', value: totalSuggestions, color: '#4f46e5' },
            { label: 'Potential Duplicates', value: removed.length, color: '#ef4444' },
            { label: 'Parameterisations', value: recommendations.length, color: '#10b981' },
            { label: 'Tokens Used', value: (tokenUsage.total_tokens || 0).toLocaleString(), color: '#06b6d4', sub: `${tokenUsage.calls || 0} API calls` },
          ].map(card => (
            <div key={card.label} style={{ padding: '10px 6px', borderRadius: 8, background: `${card.color}08`, border: `1px solid ${card.color}20`, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{card.label}</div>
              {card.sub && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{card.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 2: Optimization Impact (dynamic) ── */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#1e293b' }}>
          Optimization Impact <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>— updates as you review</span>
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
          {[
            { label: 'Optimisable TCs', value: recommendations.reduce((s, rec) => s + (rec.source_tc_ids || []).length, 0), color: '#f97316' },
            { label: 'Optimised TCs', value: recommendations.reduce((s, rec, i) => (decisions.recommendations[i] || 'accepted') === 'accepted' ? s + (rec.source_tc_ids || []).length : s, 0), color: '#84cc16' },
            { label: 'Accepted', value: `${recCounts.accepted + removalCounts.accepted} / ${totalSuggestions}`, color: '#3b82f6' },
            { label: 'Suite Optimised', value: `${stepsRemovedPct}%`, color: '#8b5cf6' },
            { label: 'Acceptance Rate', value: `${acceptanceRate}%`, color: '#14b8a6' },
          ].map(card => (
            <div key={card.label} style={{ padding: '10px 6px', borderRadius: 8, background: `${card.color}08`, border: `1px solid ${card.color}20`, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{card.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 3: Cost Savings ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#1e293b' }}>
          Cost Savings Estimate <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>— based on accepted optimizations</span>
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ padding: '14px 10px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(249,115,22,0.06), rgba(249,115,22,0.02))', border: '1px solid rgba(249,115,22,0.15)', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#f97316', lineHeight: 1 }}>{savings.totalHoursSaved.toFixed(1)}<span style={{ fontSize: 13, fontWeight: 600 }}> hrs</span></div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#f97316', marginTop: 6, textTransform: 'uppercase' }}>Total Hours Saved</div>
            <div style={{ fontSize: 9, color: '#f97316', marginTop: 2 }}>per execution cycle</div>
          </div>
          <div style={{ padding: '14px 10px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))', border: '1px solid rgba(34,197,94,0.2)', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>${savings.dollarSavings.toFixed(0)}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#16a34a', marginTop: 6, textTransform: 'uppercase' }}>Dollar Savings</div>
            <div style={{ fontSize: 9, color: '#16a34a', marginTop: 2 }}>per execution cycle</div>
          </div>
        </div>
      </div>

      {/* ── Review Tabs ── */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 12 }}>
        {[
          { key: 'summary', label: 'Summary' },
          { key: 'removed', label: `Removals (${removed.length})` },
          { key: 'recommendations', label: `Recommendations (${recommendations.length})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: 'none', background: 'transparent',
            color: activeTab === tab.key ? '#4f46e5' : '#64748b',
            borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: '-2px',
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Summary tab */}
      {activeTab === 'summary' && (
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 8px' }}>
            <strong>{summary?.original_count || 0}</strong> test cases with <strong>{summary?.original_step_count || 0}</strong> total steps analyzed.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong style={{ color: '#ef4444' }}>{summary?.total_removed || 0}</strong> redundant test case(s) identified for removal.{' '}
            <strong style={{ color: '#f59e0b' }}>{summary?.recommendations_count || 0}</strong> optimization recommendation(s) generated.
          </p>
          {summary?.reduction_pct > 0 && (
            <p style={{ margin: '0 0 8px' }}>
              Potential reduction: <strong style={{ color: '#10b981' }}>{summary.reduction_pct}%</strong> of the test suite.
            </p>
          )}
          {summary?.coverage_pct != null && (
            <p style={{ margin: 0 }}>Coverage: <strong>{summary.coverage_pct}%</strong>{summary.coverage_complete ? ' ✓ Complete' : ''}</p>
          )}
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#94a3b8' }}>
            Completed {fmtTime(job.completed_at)} &bull; Processing time: {fmtElapsed(job.started_at, job.completed_at)}
          </p>
        </div>
      )}

      {/* Removals tab with accept/reject */}
      {activeTab === 'removed' && (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {removed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748b', fontSize: 13 }}>No redundant test cases found.</div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                Click to toggle accept/reject. Green = accept removal, Red = reject (keep TC).
              </div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                    {idCol && <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>ID</th>}
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Title</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Reason</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', width: 80 }}>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {removed.map((r, i) => {
                    const tcId = idCol ? String(r[idCol] || '') : String(i);
                    const decision = decisions.removals[tcId] || 'accepted';
                    return (
                      <React.Fragment key={i}>
                      <tr style={{ borderBottom: '1px solid #f1f5f9', background: decision === 'rejected' ? '#fef2f2' : 'transparent' }}>
                        {idCol && <td style={{ padding: '8px 10px', fontWeight: 500, color: '#1e293b' }}>{r[idCol] || ''}</td>}
                        <td style={{ padding: '8px 10px', color: '#1e293b' }}>{r.Title || r.title || Object.values(r)[1] || ''}</td>
                        <td style={{ padding: '8px 10px', color: '#64748b', fontSize: 11 }}>{r._removal_reason || r._similar_to || 'Duplicate'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button onClick={() => setDecisions(prev => ({ ...prev, removals: { ...prev.removals, [tcId]: 'accepted' } }))} style={{
                              padding: '4px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                              background: decision === 'accepted' ? '#dcfce7' : '#f1f5f9',
                              color: decision === 'accepted' ? '#16a34a' : '#64748b',
                            }}>
                              Remove
                            </button>
                            <button onClick={() => setDecisions(prev => ({ ...prev, removals: { ...prev.removals, [tcId]: 'rejected' } }))} style={{
                              padding: '4px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                              background: decision === 'rejected' ? '#fee2e2' : '#f1f5f9',
                              color: decision === 'rejected' ? '#dc2626' : '#64748b',
                            }}>
                              Keep
                            </button>
                          </div>
                        </td>
                      </tr>
                      {decision === 'rejected' && (
                        <tr key={`fb-${i}`}>
                          <td colSpan={idCol ? 4 : 3} style={{ padding: '4px 10px 8px' }}>
                            {!feedbackConfirmed[`removal-${tcId}`] ? (
                              <div className="ro-reject-feedback-inline">
                                <input type="text" className="ro-reject-feedback-input"
                                  placeholder="Why keep both? (optional, helps improve future suggestions)"
                                  value={rejectFeedback[`removal-${tcId}`] || ''}
                                  onChange={e => setRejectFeedback(p => ({ ...p, [`removal-${tcId}`]: e.target.value }))} />
                                <button className="ro-reject-feedback-confirm" title="Confirm feedback"
                                  onClick={() => setFeedbackConfirmed(p => ({ ...p, [`removal-${tcId}`]: true }))}>{'\u2713'}</button>
                              </div>
                            ) : rejectFeedback[`removal-${tcId}`] ? (
                              <div className="ro-reject-feedback-confirmed">Feedback saved: {rejectFeedback[`removal-${tcId}`]}</div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Recommendations tab with accept/reject */}
      {activeTab === 'recommendations' && (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {recommendations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748b', fontSize: 13 }}>No recommendations generated.</div>
          ) : (
            recommendations.map((rec, i) => {
              const decision = decisions.recommendations[i] || 'accepted';
              return (
                <div key={i} style={{
                  padding: '12px 14px', marginBottom: 8, borderRadius: 8,
                  background: decision === 'rejected' ? '#fef2f2' : '#f8fafc',
                  border: decision === 'rejected' ? '1px solid #fecaca' : '1px solid #e2e8f0',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Chip label={rec.type || 'optimize'} size="small" sx={{
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      color: rec.type === 'parameterize' ? '#4f46e5' : rec.type === 'club' ? '#f59e0b' : '#ef4444',
                      bgcolor: rec.type === 'parameterize' ? '#eef2ff' : rec.type === 'club' ? '#fffbeb' : '#fef2f2',
                      height: 22,
                    }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', flex: 1 }}>
                      TCs: {(rec.source_tc_ids || []).join(', ')}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setDecisions(prev => ({ ...prev, recommendations: { ...prev.recommendations, [i]: 'accepted' } }))} style={{
                        padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                        background: decision === 'accepted' ? '#dcfce7' : '#f1f5f9',
                        color: decision === 'accepted' ? '#16a34a' : '#64748b',
                      }}>
                        Accept
                      </button>
                      <button onClick={() => setDecisions(prev => ({ ...prev, recommendations: { ...prev.recommendations, [i]: 'rejected' } }))} style={{
                        padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                        background: decision === 'rejected' ? '#fee2e2' : '#f1f5f9',
                        color: decision === 'rejected' ? '#dc2626' : '#64748b',
                      }}>
                        Reject
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>{rec.reason || ''}</div>
                  {decision === 'rejected' && (
                    <div style={{ marginTop: 8 }}>
                      {!feedbackConfirmed[`rec-${i}`] ? (
                        <div className="ro-reject-feedback-inline">
                          <input type="text" className="ro-reject-feedback-input"
                            placeholder="Why did you reject? (optional, helps improve future suggestions)"
                            value={rejectFeedback[`rec-${i}`] || ''}
                            onChange={e => setRejectFeedback(p => ({ ...p, [`rec-${i}`]: e.target.value }))} />
                          <button className="ro-reject-feedback-confirm" title="Confirm feedback"
                            onClick={() => setFeedbackConfirmed(p => ({ ...p, [`rec-${i}`]: true }))}>{'\u2713'}</button>
                        </div>
                      ) : rejectFeedback[`rec-${i}`] ? (
                        <div className="ro-reject-feedback-confirmed">Feedback saved: {rejectFeedback[`rec-${i}`]}</div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', alignItems: 'center' }}>
        <button onClick={handleClear} disabled={clearing} style={{
          padding: '8px 20px', fontSize: 12, fontWeight: 600, borderRadius: 6,
          border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer',
        }}>{clearing ? 'Clearing...' : 'Clear Slot'}</button>
        <button onClick={handleFinalize} disabled={finalizing} style={{
          padding: '10px 24px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
          background: finalizing ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5, #6366f1)',
          color: '#fff', cursor: finalizing ? 'not-allowed' : 'pointer',
          boxShadow: finalizing ? 'none' : '0 2px 8px rgba(79,70,229,0.25)',
        }}>{finalizing ? 'Generating...' : 'Finalize & Download'}</button>
      </div>
    </div>
  );
}

// ── Failed Slot ──

function FailedSlot({ job, onRefresh }) {
  const [clearing, setClearing] = useState(false);
  const handleClear = async () => {
    setClearing(true);
    try { await regressionOptimizerApi.deleteScheduledJob(job.id); onRefresh(); }
    catch { setClearing(false); }
  };
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>&#10060;</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Optimization Failed</div>
      <div style={{ fontSize: 12, color: '#dc2626', maxWidth: 500, margin: '0 auto 16px' }}>
        {job.error_message || 'An unknown error occurred.'}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>
        Failed at {fmtTime(job.completed_at)} &bull; Processing time: {fmtElapsed(job.started_at, job.completed_at)}
      </div>
      <button onClick={handleClear} disabled={clearing} style={{
        padding: '8px 24px', fontSize: 13, fontWeight: 600, borderRadius: 8,
        border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: clearing ? 'not-allowed' : 'pointer',
      }}>{clearing ? 'Clearing...' : 'Clear Slot'}</button>
    </div>
  );
}
